// src/components/resources/glossary/GlossaryEmptyState.tsx

'use client'

import { motion } from 'motion/react'
import RiSearchLine from '~/icons/remix/generated/RiSearchLine'
import RiFilterLine from '~/icons/remix/generated/RiFilterLine'
import type { GlossaryCategory } from '~/lib/resources/types/shared/enums'

interface GlossaryEmptyStateProps {
  category: {
    id: GlossaryCategory
    label: string
    description: string
    icon: string
  }
  hasFilters?: boolean
  /** Reset the active filters (client-side) instead of reloading the page. */
  onClearFilters?: () => void
}

/** Scroll the glossary search field into view and focus it. */
function focusGlossarySearch(): void {
  const input = document.getElementById('glossary-search')
  if (input instanceof HTMLInputElement) {
    input.scrollIntoView({ behavior: 'smooth', block: 'center' })
    input.focus({ preventScroll: true })
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

export function GlossaryEmptyState({ category, hasFilters, onClearFilters }: GlossaryEmptyStateProps): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-2xl bg-white p-12 text-center shadow-sm ring-1 ring-gray-200 dark:bg-gray-800/50 dark:ring-gray-800"
    >
      <div className="mx-auto max-w-sm">
        <div className="flex justify-center mb-6">
          <span className="text-4xl">{category.icon}</span>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {hasFilters 
            ? 'No matching terms found'
            : `No terms found in ${category.label}`
          }
        </h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {hasFilters 
            ? "Try adjusting your search or filters to find what you're looking for."
            : "We're constantly adding new terms to our glossary. Check back soon or try searching for something else."
          }
        </p>
        <div className="mt-6 flex justify-center gap-4">
          {hasFilters && (
            <button
              type="button"
              onClick={() => (onClearFilters ? onClearFilters() : window.location.reload())}
              className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-700"
            >
              <RiFilterLine className="h-5 w-5" />
              Clear filters
            </button>
          )}
          <button
            type="button"
            onClick={focusGlossarySearch}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-4 py-2.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
          >
            <RiSearchLine className="h-5 w-5" />
            Search glossary
          </button>
        </div>
      </div>
    </motion.div>
  )
} 